// Database connection utility using MySQL2 with raw SQL queries
import mysql from 'mysql2/promise';

// User type
export type User = {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  passwordHash: string;
  profilePicture?: string | null;
  role: 'user' | 'trainer' | 'gym' | 'admin';
  status: 'active' | 'suspended' | 'deleted';
  createdAt: Date;
  updatedAt: Date;
};

// Gym type
export type Gym = {
  id: string;
  ownerEmail: string;
  firstName: string;
  businessName?: string | null;
  phone: string;
  location: string;
  aboutGym: string;
  photos: string[];
  services: string[];
  membershipPricing?: string | null;
  membershipDescription: string;
  operatingHours: {
    [key: string]: {
      isOpen: boolean;
      openTime: string;
      closeTime: string;
    };
  };
  facilityFeatures: string[];
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  updatedAt: Date;
};

// Trainer type
export type Trainer = {
  id: string;
  ownerEmail: string;
  firstName: string;
  lastName: string;
  phone: string;
  location: string;
  dateOfBirth: Date;
  bio: string;
  profilePhoto: string;
  certifications: string[];
  yearsOfExperience: number;
  specializations: string[];
  servicesOffered: string[];
  sessionPricing?: string | null;
  availability: {
    [key: string]: {
      isAvailable: boolean;
      timeSlots: string[];
    };
  };
  preferredEnvironment: string[];
  travelDistance?: string | null;
  additionalNotes?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  updatedAt: Date;
};

// Create MySQL connection pool
const pool = mysql.createPool({
  host: process.env.DB_HOST || 'localhost',
  user: process.env.DB_USER || 'root',
  password: process.env.DB_PASSWORD || '',
  database: process.env.DB_NAME || 'female_fitness_finder',
  port: parseInt(process.env.DB_PORT || '3306'),
  waitForConnections: true,
  connectionLimit: 10,
  queueLimit: 0,
  connectTimeout: 60000, // 60 seconds
  enableKeepAlive: true,
  keepAliveInitialDelay: 0,
});

// Generate sequential ID in format fff-XXX
async function generateId(): Promise<string> {
  try {
    // Get the highest ID number from database
    const [rows] = await pool.execute<any[]>(
      "SELECT id FROM users WHERE id LIKE 'fff-%' ORDER BY CAST(SUBSTRING(id, 5) AS UNSIGNED) DESC LIMIT 1"
    );

    let nextNumber = 100; // Start from fff-100

    if (rows.length > 0 && rows[0].id) {
      const lastId = rows[0].id;
      const lastNumber = parseInt(lastId.replace('fff-', ''));
      nextNumber = lastNumber + 1;
    }

    return `fff-${nextNumber}`;
  } catch (error) {
    console.error('Error generating ID:', error);
    // Fallback to timestamp-based ID if query fails
    return `fff-${Date.now()}`;
  }
}

// Database operations
export const db = {
  users: {
    create: async (data: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'profilePicture'>): Promise<User> => {
      const id = await generateId();
      const now = new Date();
      
      await pool.execute(
        `INSERT INTO users (id, full_name, email, phone, password_hash, role, created_at, updated_at) 
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, data.fullName, data.email, data.phone, data.passwordHash, data.role || 'user', now, now]
      );

      return {
        id,
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        passwordHash: data.passwordHash,
        role: data.role || 'user',
        status: data.status || 'active',
        createdAt: now,
        updatedAt: now,
      };
    },

    findByEmail: async (email: string): Promise<User | null> => {
      const [rows] = await pool.execute<any[]>(
        'SELECT * FROM users WHERE email = ? LIMIT 1',
        [email.toLowerCase()]
      );

      if (rows.length === 0) return null;

      const row = rows[0];
      return {
        id: row.id,
        fullName: row.full_name,
        email: row.email,
        phone: row.phone,
        passwordHash: row.password_hash,
        profilePicture: row.profile_picture || null,
        role: row.role || 'user',
        status: row.status || 'active',
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
      };
    },

    findById: async (id: string): Promise<User | null> => {
      const [rows] = await pool.execute<any[]>(
        'SELECT * FROM users WHERE id = ? LIMIT 1',
        [id]
      );

      if (rows.length === 0) return null;

      const row = rows[0];
      return {
        id: row.id,
        fullName: row.full_name,
        email: row.email,
        phone: row.phone,
        passwordHash: row.password_hash,
        profilePicture: row.profile_picture || null,
        role: row.role || 'user',
        status: row.status || 'active',
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
      };
    },

    updateProfilePicture: async (id: string, profilePicture: string | null): Promise<void> => {
      await pool.execute(
        'UPDATE users SET profile_picture = ?, updated_at = ? WHERE id = ?',
        [profilePicture, new Date(), id]
      );
    },

    // Admin functions
    createAdmin: async (data: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'profilePicture'>): Promise<User> => {
      const id = await generateId();
      const now = new Date();
      
      await pool.execute(
        `INSERT INTO users (id, full_name, email, phone, password_hash, role, created_at, updated_at) 
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, data.fullName, data.email, data.phone, data.passwordHash, 'admin', now, now]
      );

      return {
        id,
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        passwordHash: data.passwordHash,
        role: 'admin',
        status: 'active',
        createdAt: now,
        updatedAt: now,
      };
    },

    getAllUsers: async (): Promise<User[]> => {
      const [rows] = await pool.execute<any[]>(
        'SELECT * FROM users ORDER BY created_at DESC'
      );

      return rows.map(row => ({
        id: row.id,
        fullName: row.full_name,
        email: row.email,
        phone: row.phone,
        passwordHash: row.password_hash,
        profilePicture: row.profile_picture || null,
        role: row.role || 'user',
        status: row.status || 'active',
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
      }));
    },

    getUsersByRole: async (role: string): Promise<User[]> => {
      const [rows] = await pool.execute<any[]>(
        'SELECT * FROM users WHERE role = ? ORDER BY created_at DESC',
        [role]
      );

      return rows.map(row => ({
        id: row.id,
        fullName: row.full_name,
        email: row.email,
        phone: row.phone,
        passwordHash: row.password_hash,
        profilePicture: row.profile_picture || null,
        role: row.role || 'user',
        status: row.status || 'active',
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
      }));
    },

    update: async (id: string, data: Partial<Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'passwordHash'>>): Promise<User | null> => {
      const updates: string[] = [];
      const values: any[] = [];

      if (data.fullName !== undefined) {
        updates.push('full_name = ?');
        values.push(data.fullName);
      }
      if (data.phone !== undefined) {
        updates.push('phone = ?');
        values.push(data.phone);
      }
      if (data.profilePicture !== undefined) {
        updates.push('profile_picture = ?');
        values.push(data.profilePicture);
      }

      if (updates.length === 0) {
        return null;
      }

      updates.push('updated_at = ?');
      values.push(new Date());
      values.push(id);

      await pool.execute(
        `UPDATE users SET ${updates.join(', ')} WHERE id = ?`,
        values
      );

      return await db.users.findById(id);
    },
  },

  gyms: {
    // Create a new gym
    create: async (gymData: Omit<Gym, 'id' | 'createdAt' | 'updatedAt'>): Promise<Gym> => {
      // Get count of existing gyms to generate sequential ID
      const [countResult] = await pool.execute<any[]>('SELECT COUNT(*) as count FROM gyms');
      const count = countResult[0].count + 1;
      const id = `fff-gym${String(count).padStart(3, '0')}`;
      
      await pool.execute(
        `INSERT INTO gyms (
          id, owner_email, first_name, business_name, phone, location,
          about_gym, photos, services, membership_pricing, membership_description,
          operating_hours, facility_features, status
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id,
          gymData.ownerEmail,
          gymData.firstName,
          gymData.businessName || null,
          gymData.phone,
          gymData.location,
          gymData.aboutGym,
          JSON.stringify(gymData.photos),
          JSON.stringify(gymData.services),
          gymData.membershipPricing || null,
          gymData.membershipDescription,
          JSON.stringify(gymData.operatingHours),
          JSON.stringify(gymData.facilityFeatures),
          gymData.status
        ]
      );

      const gym = await db.gyms.findById(id);
      if (!gym) throw new Error('Failed to create gym');
      return gym;
    },

    // Find gym by ID
    findById: async (id: string): Promise<Gym | null> => {
      const [rows] = await pool.execute<any[]>(
        'SELECT * FROM gyms WHERE id = ?',
        [id]
      );

      if (rows.length === 0) return null;

      const row = rows[0];
      return {
        id: row.id,
        ownerEmail: row.owner_email,
        firstName: row.first_name,
        businessName: row.business_name,
        phone: row.phone,
        location: row.location,
        aboutGym: row.about_gym,
        photos: JSON.parse(row.photos || '[]'),
        services: JSON.parse(row.services || '[]'),
        membershipPricing: row.membership_pricing,
        membershipDescription: row.membership_description,
        operatingHours: JSON.parse(row.operating_hours || '{}'),
        facilityFeatures: JSON.parse(row.facility_features || '[]'),
        status: row.status,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      };
    },

    // Find gyms by owner email
    findByOwnerEmail: async (email: string): Promise<Gym[]> => {
      const [rows] = await pool.execute<any[]>(
        'SELECT * FROM gyms WHERE owner_email = ? ORDER BY created_at DESC',
        [email]
      );

      return rows.map(row => ({
        id: row.id,
        ownerEmail: row.owner_email,
        firstName: row.first_name,
        businessName: row.business_name,
        phone: row.phone,
        location: row.location,
        aboutGym: row.about_gym,
        photos: JSON.parse(row.photos || '[]'),
        services: JSON.parse(row.services || '[]'),
        membershipPricing: row.membership_pricing,
        membershipDescription: row.membership_description,
        operatingHours: JSON.parse(row.operating_hours || '{}'),
        facilityFeatures: JSON.parse(row.facility_features || '[]'),
        status: row.status,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    },

    // Get all gyms (with optional status filter)
    findAll: async (status?: 'pending' | 'approved' | 'rejected'): Promise<Gym[]> => {
      let query = 'SELECT * FROM gyms';
      const params: any[] = [];

      if (status) {
        query += ' WHERE status = ?';
        params.push(status);
      }

      query += ' ORDER BY created_at DESC';

      const [rows] = await pool.execute<any[]>(query, params);

      return rows.map(row => ({
        id: row.id,
        ownerEmail: row.owner_email,
        firstName: row.first_name,
        businessName: row.business_name,
        phone: row.phone,
        location: row.location,
        aboutGym: row.about_gym,
        photos: JSON.parse(row.photos || '[]'),
        services: JSON.parse(row.services || '[]'),
        membershipPricing: row.membership_pricing,
        membershipDescription: row.membership_description,
        operatingHours: JSON.parse(row.operating_hours || '{}'),
        facilityFeatures: JSON.parse(row.facility_features || '[]'),
        status: row.status,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    },

    // Update gym status
    updateStatus: async (id: string, status: 'pending' | 'approved' | 'rejected'): Promise<Gym | null> => {
      await pool.execute(
        'UPDATE gyms SET status = ? WHERE id = ?',
        [status, id]
      );

      return await db.gyms.findById(id);
    },
  },

  trainers: {
    // Create a new trainer
    create: async (trainerData: Omit<Trainer, 'id' | 'createdAt' | 'updatedAt'>): Promise<Trainer> => {
      // Get count of existing trainers to generate sequential ID
      const [countResult] = await pool.execute<any[]>('SELECT COUNT(*) as count FROM trainers');
      const count = countResult[0].count + 1;
      const id = `fff-trainer${String(count).padStart(3, '0')}`;
      
      await pool.execute(
        `INSERT INTO trainers (
          id, owner_email, first_name, last_name, phone, location, date_of_birth,
          bio, profile_photo, certifications, years_of_experience,
          specializations, services_offered, session_pricing,
          availability, preferred_environment, travel_distance, additional_notes, status
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id,
          trainerData.ownerEmail,
          trainerData.firstName,
          trainerData.lastName,
          trainerData.phone,
          trainerData.location,
          trainerData.dateOfBirth,
          trainerData.bio,
          trainerData.profilePhoto,
          JSON.stringify(trainerData.certifications),
          trainerData.yearsOfExperience,
          JSON.stringify(trainerData.specializations),
          JSON.stringify(trainerData.servicesOffered),
          trainerData.sessionPricing || null,
          JSON.stringify(trainerData.availability),
          JSON.stringify(trainerData.preferredEnvironment),
          trainerData.travelDistance || null,
          trainerData.additionalNotes || null,
          trainerData.status
        ]
      );

      const trainer = await db.trainers.findById(id);
      if (!trainer) throw new Error('Failed to create trainer');
      return trainer;
    },

    // Find trainer by ID
    findById: async (id: string): Promise<Trainer | null> => {
      const [rows] = await pool.execute<any[]>(
        'SELECT * FROM trainers WHERE id = ?',
        [id]
      );

      if (rows.length === 0) return null;

      const row = rows[0];
      return {
        id: row.id,
        ownerEmail: row.owner_email,
        firstName: row.first_name,
        lastName: row.last_name,
        phone: row.phone,
        location: row.location,
        dateOfBirth: row.date_of_birth,
        bio: row.bio,
        profilePhoto: row.profile_photo,
        certifications: JSON.parse(row.certifications || '[]'),
        yearsOfExperience: row.years_of_experience,
        specializations: JSON.parse(row.specializations || '[]'),
        servicesOffered: JSON.parse(row.services_offered || '[]'),
        sessionPricing: row.session_pricing,
        availability: JSON.parse(row.availability || '{}'),
        preferredEnvironment: JSON.parse(row.preferred_environment || '[]'),
        travelDistance: row.travel_distance,
        additionalNotes: row.additional_notes,
        status: row.status,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      };
    },

    // Find trainers by owner email
    findByOwnerEmail: async (email: string): Promise<Trainer[]> => {
      const [rows] = await pool.execute<any[]>(
        'SELECT * FROM trainers WHERE owner_email = ? ORDER BY created_at DESC',
        [email]
      );

      return rows.map(row => ({
        id: row.id,
        ownerEmail: row.owner_email,
        firstName: row.first_name,
        lastName: row.last_name,
        phone: row.phone,
        location: row.location,
        dateOfBirth: row.date_of_birth,
        bio: row.bio,
        profilePhoto: row.profile_photo,
        certifications: JSON.parse(row.certifications || '[]'),
        yearsOfExperience: row.years_of_experience,
        specializations: JSON.parse(row.specializations || '[]'),
        servicesOffered: JSON.parse(row.services_offered || '[]'),
        sessionPricing: row.session_pricing,
        availability: JSON.parse(row.availability || '{}'),
        preferredEnvironment: JSON.parse(row.preferred_environment || '[]'),
        travelDistance: row.travel_distance,
        additionalNotes: row.additional_notes,
        status: row.status,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    },

    // Get all trainers (with optional status filter)
    findAll: async (status?: 'pending' | 'approved' | 'rejected'): Promise<Trainer[]> => {
      let query = 'SELECT * FROM trainers';
      const params: any[] = [];

      if (status) {
        query += ' WHERE status = ?';
        params.push(status);
      }

      query += ' ORDER BY created_at DESC';

      const [rows] = await pool.execute<any[]>(query, params);

      return rows.map(row => ({
        id: row.id,
        ownerEmail: row.owner_email,
        firstName: row.first_name,
        lastName: row.last_name,
        phone: row.phone,
        location: row.location,
        dateOfBirth: row.date_of_birth,
        bio: row.bio,
        profilePhoto: row.profile_photo,
        certifications: JSON.parse(row.certifications || '[]'),
        yearsOfExperience: row.years_of_experience,
        specializations: JSON.parse(row.specializations || '[]'),
        servicesOffered: JSON.parse(row.services_offered || '[]'),
        sessionPricing: row.session_pricing,
        availability: JSON.parse(row.availability || '{}'),
        preferredEnvironment: JSON.parse(row.preferred_environment || '[]'),
        travelDistance: row.travel_distance,
        additionalNotes: row.additional_notes,
        status: row.status,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    },

    // Update trainer status
    updateStatus: async (id: string, status: 'pending' | 'approved' | 'rejected'): Promise<Trainer | null> => {
      await pool.execute(
        'UPDATE trainers SET status = ? WHERE id = ?',
        [status, id]
      );

      return await db.trainers.findById(id);
    },
  },
};

// Export pool for direct queries if needed
export { pool };
